import { Avatar, Button, OverflowMenu } from 'src/design-system'
import * as AlertDialog from '@radix-ui/react-alert-dialog'
import { DownloadSimple, Eye, Trash, UsersThree } from '@phosphor-icons/react'
import { observer } from 'mobx-react-lite'
import * as React from 'react'
import { CheckinStatusLabel } from 'components/checkin-status-label'
import { SearchFilter } from 'components/search-filter'
import { Select } from 'components/atoms/select'
import { store } from 'store/index'
import { useStore } from 'store/context'
import * as SecondaryNav from 'components/secondary-nav'
import * as Table from 'components/table'
import { Checkin } from 'store/modules/checkins'
import { User } from 'store/modules/users'
import { successToast } from 'app/packs/src/utils/success-toast'
import { errorToast } from 'app/packs/src/utils/error-toast'
import { fuzzyFilter } from 'app/packs/src/utils/table-helpers'
import { CheckinsDownloadButton } from '../checkins-page/download-button'
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  flexRender,
  CellContext,
} from '@tanstack/react-table'

type ArchivedCheckinRow = {
  userId: string
  authorName: string
  authorAvatarUrl?: string
  currentRole?: string
  teamId?: string
  teamName?: string
  archivedCheckinCount: number
  lastCheckinResult?: string | null
  lastCheckinStatusLabel?: string | null
  lastCheckinStatusColor?: string | null
  lastCheckinPercentageDifference?: number | null
  lastCheckinSlug?: string
}

const columnHelper = createColumnHelper<ArchivedCheckinRow>()

const columns = [
  columnHelper.accessor('authorName', {
    header: 'Name',
    cell: NameCell,
    id: 'name',
  }),
  columnHelper.accessor('teamId', {
    header: 'Team',
    id: 'team',
    enableGlobalFilter: false,
    filterFn: 'arrIncludesSome',
  }),
  columnHelper.accessor('archivedCheckinCount', {
    header: 'Number of check-ins',
    cell: (data) => data.getValue(),
    id: 'checkinCount',
  }),
  columnHelper.accessor('lastCheckinResult', {
    header: 'Last checkin result',
    cell: ResultCell,
    id: 'lastResult',
  }),
  columnHelper.display({
    cell: ActionsCell,
    id: 'actions',
  }),
]

export const ArchivedCheckinsPage = observer(() => {
  const [loading, setLoading] = React.useState(true)
  const { currentUser } = useStore()

  React.useEffect(() => {
    const fetchData = async () => {
      await Promise.all([
        store.users.fetchAll({
          page: { size: 9999 },
          include: ['position', 'team'],
        }),
        store.checkins.fetchAll({
          filter: { archived: true },
          exclude: ['actions', 'checkin_skills', 'position'],
          page: { size: 9999 },
        }),
      ])
      setLoading(false)
    }
    fetchData()
  }, [])

  const archivedCheckins = React.useMemo(() => {
    if (loading) return []
    return store.checkins.filtered((c) => c.isArchived)
  }, [loading, store.checkins.all.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const rows: ArchivedCheckinRow[] = React.useMemo(() => {
    const byUser = new Map<string, Checkin[]>()
    for (const checkin of archivedCheckins) {
      const userId = checkin.author.id
      const list = byUser.get(userId) || []
      list.push(checkin)
      byUser.set(userId, list)
    }

    return Array.from(byUser.entries()).map(([userId, checkins]) => {
      const user = store.users.byId(userId) as User
      const sorted = [...checkins].sort((a, b) => {
        if (!a.finalisedAt && !b.finalisedAt) return 0
        if (!a.finalisedAt) return 1
        if (!b.finalisedAt) return -1
        return a.finalisedAt > b.finalisedAt ? -1 : 1
      })
      const lastCheckin = sorted[0]

      return {
        userId,
        authorName: user?.fullName || 'Unknown',
        authorAvatarUrl: user?.avatarUrl || undefined,
        currentRole: user?.position?.name,
        teamId: user?.team?.id,
        teamName: user?.team?.name,
        archivedCheckinCount: checkins.length,
        lastCheckinResult: lastCheckin?.summaryStatus,
        lastCheckinStatusLabel: lastCheckin?.summaryStatusLabel,
        lastCheckinStatusColor: lastCheckin?.summaryStatusColor,
        lastCheckinPercentageDifference:
          lastCheckin?.summaryPercentageDifference,
        lastCheckinSlug: lastCheckin?.slug,
      }
    })
  }, [archivedCheckins])

  const [globalFilter, setGlobalFilter] = React.useState('')

  const teamOptions = React.useMemo(() => {
    return store.teams.sortedForCurrentUser.map((team) => ({
      label: team.name,
      value: team.id,
    }))
  }, [store.teams.sortedForCurrentUser]) // eslint-disable-line react-hooks/exhaustive-deps

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: fuzzyFilter,
    state: {
      globalFilter,
      columnVisibility: { team: false },
    },
    onGlobalFilterChange: setGlobalFilter,
  })

  const csvData = React.useCallback(() => {
    const lines: string[] = []
    lines.push(
      'Name,Email,Current Role,Team,Number of Archived Check-ins,Last Check-in Result'
    )
    for (const row of table.getRowModel().rows) {
      const r = row.original
      const user = store.users.byId(r.userId)
      const resultLabel = r.lastCheckinResult
        ? r.lastCheckinResult
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase())
        : '-'
      const pctText =
        r.lastCheckinPercentageDifference != null
          ? `${r.lastCheckinPercentageDifference}%`
          : ''
      const resultWithPct = pctText
        ? `${resultLabel} (${pctText})`
        : resultLabel
      lines.push(
        [
          r.authorName,
          user?.email || '-',
          r.currentRole || '-',
          r.teamName || '-',
          r.archivedCheckinCount,
          resultWithPct,
        ].join(',')
      )
    }
    return lines.join('%0A')
  }, [table])

  if (!currentUser) return null

  const showAdminManagerContent = currentUser.isAdmin || currentUser.isManager

  return (
    <>
      <SecondaryNav.Root>
        <SecondaryNav.Header className="flex flex-row items-center justify-between w-full text-[16px] leading-5">
          Archived
          {showAdminManagerContent && (
            <CheckinsDownloadButton csvData={csvData} />
          )}
        </SecondaryNav.Header>
      </SecondaryNav.Root>
      <main className="pt-4 lg:pt-10 overflow-x-auto flex-1 mb-10">
        <div className="flex gap-x-3 mb-5 px-4">
          <SearchFilter
            name="archived-checkins-search"
            onChange={(value) => setGlobalFilter(String(value))}
            value={globalFilter ?? ''}
          />
          <Select
            isMulti
            isSearchable
            onChange={(values: string[]) =>
              table.getColumn('team')?.setFilterValue(values)
            }
            options={teamOptions}
            triggerContent={<TeamTriggerContent />}
            value={
              table.getState().columnFilters.find((c) => c.id === 'team')
                ?.value as string[] | undefined
            }
          />
        </div>
        <div className="overflow-x-auto pb-10">
          <Table.Root>
            <Table.Head>
              {table.getHeaderGroups().map((headerGroup) => (
                <Table.Row key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Table.Header
                      key={header.id}
                      header={header}
                      source="archived-checkins-table"
                      loading={loading}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </Table.Header>
                  ))}
                </Table.Row>
              ))}
            </Table.Head>
            <Table.Body>
              {table.getRowModel().rows.length === 0 && (
                <Table.NoResultsRow colspan={table.getAllColumns().length} />
              )}
              {table.getRowModel().rows.map((row, index) => (
                <Table.Row
                  showBottomBorder={index < table.getRowModel().rows.length - 1}
                  key={row.id}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Table.Cell key={cell.id} className="whitespace-nowrap">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </div>
      </main>
    </>
  )
})

function NameCell(data: CellContext<ArchivedCheckinRow, unknown>) {
  const { authorName, authorAvatarUrl, currentRole } = data.row.original
  const [fname, lname] = authorName.split(' ')
  const initials = fname[0] + (lname ? lname[0] : '')

  return (
    <div className="flex flex-row gap-x-3 items-center">
      <Avatar
        size="sm"
        className="flex-shrink-0"
        fallbackText={initials}
        src={authorAvatarUrl}
        alt={`Avatar for ${authorName}`}
      />
      <div className="flex flex-col">
        <p className="font-bold m-0 w-max">{authorName}</p>
        {currentRole && (
          <span className="text-gray-600 text-xs">{currentRole}</span>
        )}
      </div>
    </div>
  )
}

function ResultCell(data: CellContext<ArchivedCheckinRow, unknown>) {
  const {
    lastCheckinResult,
    lastCheckinStatusLabel,
    lastCheckinStatusColor,
    lastCheckinPercentageDifference,
  } = data.row.original

  if (!lastCheckinResult) return <span className="text-gray-400">-</span>

  let differenceText = ''
  if (lastCheckinPercentageDifference != null) {
    if (lastCheckinPercentageDifference > 0) differenceText = ' above'
    if (lastCheckinPercentageDifference < 0) differenceText = ' away'
  }

  return (
    <div className="flex gap-2 h-full items-center w-max">
      <CheckinStatusLabel
        status={lastCheckinResult}
        statusLabel={lastCheckinStatusLabel}
        statusColor={lastCheckinStatusColor}
      />
      {differenceText !== '' && lastCheckinPercentageDifference != null && (
        <p className="mb-0 text-xs">
          {lastCheckinPercentageDifference}%{differenceText}
        </p>
      )}
    </div>
  )
}

function ActionsCell(data: CellContext<ArchivedCheckinRow, unknown>) {
  const { userId, authorName, lastCheckinSlug } = data.row.original
  const isAdmin = !!store.currentUser?.isAdmin
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)

  const archivedCheckins = React.useMemo(
    () =>
      store.checkins.filtered((c) => c.isArchived && c.author.id === userId),
    [userId, store.checkins.all.length] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const onDelete = React.useCallback(async () => {
    const ids = archivedCheckins.map((c) => c.id)
    const result = await store.checkins.bulkDestroy(ids)
    if (result.success) {
      successToast(`Archived check-ins for ${authorName} deleted!`)
    } else {
      errorToast()
    }
  }, [archivedCheckins, authorName])

  return (
    <>
      <div className="flex flex-row gap-4 min-w-[50px]">
        <OverflowMenu.Root right>
          <OverflowMenu.Item>
            <a
              className="flex flex-row items-center gap-x-1 text-gray-900"
              href={`/check-ins/archived/${userId}`}
            >
              <Eye
                aria-hidden
                className="text-gray-600 w-3.5 h-3.5"
                weight="bold"
              />
              View
            </a>
          </OverflowMenu.Item>
          {lastCheckinSlug && (
            <OverflowMenu.Item>
              <a
                className="flex flex-row items-center gap-x-1 text-gray-900"
                href={`/checkins/${lastCheckinSlug}/csv`}
              >
                <DownloadSimple
                  aria-hidden
                  className="text-gray-600 w-3.5 h-3.5"
                  weight="bold"
                />
                Download
              </a>
            </OverflowMenu.Item>
          )}
          {isAdmin && (
            <OverflowMenu.Item
              variant="destructive"
              onClick={() => setDeleteDialogOpen(true)}
            >
              <div className="flex flex-row items-center gap-x-1">
                <Trash
                  aria-hidden
                  className="text-gray-600 w-3.5 h-3.5"
                  weight="bold"
                />
                Delete
              </div>
            </OverflowMenu.Item>
          )}
        </OverflowMenu.Root>
      </div>
      <AlertDialog.Root
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
      >
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="fixed inset-0 z-dialog bg-black/40" />
          <AlertDialog.Content className="fixed z-dialog bg-white rounded-lg p-8 border border-gray-200 w-screen m-4 max-w-lg shadow-modal top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <AlertDialog.Title className="font-bold text-[20px] leading-7 mb-2">
              Are you sure you want to delete this user&apos;s check-in data?
            </AlertDialog.Title>
            <AlertDialog.Description className="text-gray-600 text-[14px] leading-5">
              Once the data is deleted, you may not undo.
            </AlertDialog.Description>
            <div className="flex justify-end mt-5 gap-2">
              <AlertDialog.Action asChild>
                <Button colourVariant="danger" onClick={onDelete}>
                  Delete
                </Button>
              </AlertDialog.Action>
              <AlertDialog.Cancel asChild>
                <Button variant="outline">Cancel</Button>
              </AlertDialog.Cancel>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </>
  )
}

const TeamTriggerContent: React.FC = () => (
  <div className="flex gap-1 items-center">
    <UsersThree aria-hidden className="h-4 text-gray-600 w-4" weight="bold" />
    All teams
  </div>
)
